"use client"

import { Loader2 } from "lucide-react"
import { useTranslations } from "next-intl"

import {
  useEmailVerification,
  type VerificationContext,
  type VerificationSurface,
} from "@/features/auth/hooks/use-email-verification"
import { Button } from "@/shared/components/ui/button"
import { cn } from "@/shared/lib/utils"

interface EmailVerificationResendProps {
  email: string
  surface: VerificationSurface
  context: VerificationContext
  mode?: "live" | "preview"
  /** Called when the email is confirmed elsewhere and the user is advanced. */
  onConfirmed: () => void
}

/**
 * The resend control for the "check your email" / unverified-login view,
 * shared by the auth modal and the register/login pages. Owns the resend
 * button, its cooldown/error/success feedback, the confirmation polling, and
 * the analytics — via `useEmailVerification` — so the fix lands in one place.
 */
export function EmailVerificationResend({
  email,
  surface,
  context,
  mode = "live",
  onConfirmed,
}: EmailVerificationResendProps) {
  const auth = useTranslations("auth.shared")
  const { resendCooldown, resendLoading, resendError, resendSuccess, handleResend } =
    useEmailVerification({ email, surface, context, mode, onConfirmed })

  const isModal = surface === "modal"
  const resendLabel =
    context === "signup"
      ? auth("resendConfirmationEmail")
      : auth("resendVerificationEmail")

  return (
    <>
      {resendError && (
        <div
          className={cn(
            isModal
              ? "rounded-xl border border-red-500/20 bg-red-500/[0.06] px-4 py-3"
              : "rounded-lg border border-destructive/30 bg-destructive/5 p-3"
          )}
        >
          <p className={cn("text-sm", isModal ? "text-red-400" : "text-destructive")}>
            {resendError}
          </p>
        </div>
      )}
      {resendSuccess && (
        <div
          className={cn(
            isModal
              ? "rounded-xl border border-emerald-500/20 bg-emerald-500/[0.06] px-4 py-3"
              : "rounded-lg border border-emerald-500/20 bg-emerald-500/[0.06] p-3"
          )}
        >
          <p className="text-sm text-emerald-400">{auth("verificationSent")}</p>
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size={isModal ? undefined : "sm"}
        className={cn(
          "w-full",
          isModal && "h-10 rounded-xl border-white/[0.08] hover:bg-white/[0.06] sm:h-11"
        )}
        disabled={resendCooldown > 0 || resendLoading}
        onClick={handleResend}
      >
        {resendLoading ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            {auth("sending")}
          </>
        ) : resendCooldown > 0 ? (
          auth("resendLinkIn", {
            time: `${Math.floor(resendCooldown / 60)}:${String(resendCooldown % 60).padStart(2, "0")}`,
          })
        ) : (
          resendLabel
        )}
      </Button>
    </>
  )
}
